/**
 * CallKit Bridge - Platform-specific call notification utilities
 * Handles CallKit (iOS) and Full-Screen Intent (Android) integrations
 */

import { Capacitor, registerPlugin } from '@capacitor/core';

// Register native plugins
const CallKitPlugin = registerPlugin('CallKitPlugin');
const VoIPPushPlugin = registerPlugin('VoIPPushPlugin');
const FullScreenIntentPermission = registerPlugin('FullScreenIntentPermission');

/**
 * Get current platform
 */
export const getPlatform = () => {
  return Capacitor.getPlatform(); // 'ios', 'android', or 'web'
};

/**
 * Check if we're running on a native platform
 */
export const isNative = () => {
  const platform = getPlatform();
  return platform === 'ios' || platform === 'android';
};

/**
 * Check if CallKit is available (iOS only)
 */
export const isCallKitAvailable = async () => {
  if (getPlatform() !== 'ios') return false;
  
  try {
    const result = await CallKitPlugin.isAvailable();
    return result.available === true;
  } catch (error) {
    console.error('❌ CallKit availability check failed:', error);
    return false;
  }
};

// ========================================
// iOS CallKit Functions
// ========================================

/**
 * Report an incoming call to CallKit (iOS)
 * @param {string} callId - Unique call identifier
 * @param {string} callerName - Name to display (e.g., "J. Smith — NYPD")
 * @returns {Promise<boolean>} Success status
 */
export const reportIncomingCall = async (callId, callerName) => {
  if (getPlatform() !== 'ios') {
    console.log('⏭️ Skipping CallKit (not iOS)');
    return null;
  }

  try {
    console.log(`📞 Reporting incoming call to CallKit: ${callId}`);
    const result = await CallKitPlugin.reportIncomingCall({
      callId,
      callerName
    });
    console.log('✅ CallKit incoming call reported');
    return result;
  } catch (error) {
    console.error('❌ Failed to report incoming call to CallKit:', error);
    return null;
  }
};

/**
 * Report an outgoing call to CallKit (iOS)
 * @param {string} callId - Unique call identifier  
 * @param {string} calleeName - Name of person being called
 * @returns {Promise<boolean>} Success status
 */
export const reportOutgoingCall = async (callId, calleeName) => {
  if (getPlatform() !== 'ios') {
    console.log('⏭️ Skipping CallKit (not iOS)');
    return null;
  }

  try {
    console.log(`📞 Reporting outgoing call to CallKit: ${callId} to ${calleeName}`);
    const result = await CallKitPlugin.reportOutgoingCall({
      callId,
      calleeName
    });
    console.log('✅ CallKit outgoing call reported');
    return result;
  } catch (error) {
    console.error('❌ Failed to report outgoing call to CallKit:', error);
    return null;
  }
};

/**
 * Update outgoing call to connected state (iOS)
 * @param {string} callId - Call identifier
 */
export const reportOutgoingCallConnected = async (callId) => {
  if (getPlatform() !== 'ios') return;

  try {
    await CallKitPlugin.reportOutgoingCallConnected({ callId });
    console.log('✅ CallKit outgoing call marked as connected');
  } catch (error) {
    console.error('❌ Failed to update outgoing call status:', error);
  }
};

/**
 * End a call in CallKit (iOS)
 * @param {string} callId - Call identifier
 * @param {string} reason - End reason: 'failed', 'remote', 'unanswered', 'declined'
 */
export const endCall = async (callId, reason = 'remote') => {
  if (getPlatform() !== 'ios') return;

  try {
    await CallKitPlugin.endCall({ callId });
    console.log(`🔴 CallKit call ended: ${callId}`);
  } catch (error) {
    console.error('❌ Failed to end call in CallKit:', error);
  }
};

/**
 * Report call ended to CallKit (iOS)
 * @param {string} callId - Call identifier
 * @param {string} reason - 'failed' | 'remote' | 'unanswered' | 'declined' | 'answeredelsewhere'
 */
export const reportCallEnded = async (callId, reason = 'remote') => {
  if (getPlatform() !== 'ios') return;

  try {
    await CallKitPlugin.reportCallEnded({ callId, reason });
    console.log(`🔴 CallKit notified of call end: ${callId} (${reason})`);
  } catch (error) {
    console.error('❌ Failed to report call ended:', error);
  }
};

/**
 * Toggle speaker route (iOS)
 * @param {boolean} speakerOn
 */
export const setCallKitSpeakerRoute = async (speakerOn) => {
  if (getPlatform() !== 'ios') return;

  try {
    await CallKitPlugin.setSpeakerRoute({ speakerOn: !!speakerOn });
  } catch (error) {
    console.error('❌ Failed to set speaker route:', error);
  }
};

/**
 * Get pending CallKit accept (iOS)
 * @returns {Promise<{callId?: string, callKitId?: string, timestamp?: number}>}
 */
export const getPendingAccept = async () => {
  if (getPlatform() !== 'ios') return {};
  try {
    const result = await CallKitPlugin.getPendingAccept();
    return result || {};
  } catch (error) {
    console.error('❌ Failed to get pending accept:', error);
    return {};
  }
};

/**
 * Clear pending CallKit accept (iOS)
 */
export const clearPendingAccept = async () => {
  if (getPlatform() !== 'ios') return;
  try {
    await CallKitPlugin.clearPendingAccept();
  } catch (error) {
    console.error('❌ Failed to clear pending accept:', error);
  }
};

/**
 * Check if a CallKit call is active (iOS)
 * @param {string} callKitId
 */
export const hasActiveCall = async (callKitId) => {
  if (getPlatform() !== 'ios') return false;
  if (!callKitId) return false;
  try {
    const result = await CallKitPlugin.hasActiveCall({ callKitId });
    return result?.active === true;
  } catch (error) {
    console.error('❌ Failed to check active CallKit call:', error);
    return false;
  }
};

/**
 * Add listener for CallKit events (iOS)
 * @param {string} eventName - 'callAccepted' | 'callEnded' | 'outgoingCallStarted' | 'audioActivated'
 * @param {Function} callback - Event handler
 * @returns {Function} Cleanup function to remove listener
 */
export const addCallKitListener = (eventName, callback) => {
  if (getPlatform() !== 'ios') {
    return () => {}; // No-op cleanup
  }

  try {
    const handle = CallKitPlugin.addListener(eventName, callback);
    console.log(`👂 CallKit listener added: ${eventName}`);
    return () => {
      handle.remove();
      console.log(`👋 CallKit listener removed: ${eventName}`);
    };
  } catch (error) {
    console.error(`❌ Failed to add CallKit listener for ${eventName}:`, error);
    return () => {};
  }
};

// ========================================
// iOS VoIP Push Functions
// ========================================

/**
 * Register for VoIP push notifications (iOS)
 * Call this once on app launch
 * @returns {Promise<string|null>} VoIP token or null
 */
export const registerForVoIPPush = async () => {
  if (getPlatform() !== 'ios') {
    console.log('⏭️ VoIP push not needed (not iOS)');
    return null;
  }

  try {
    console.log('📲 Registering for VoIP push...');
    const result = await VoIPPushPlugin.register();
    
    if (result.token) {
      console.log(`✅ VoIP token received: ${result.token.substring(0, 20)}...`);
      return result.token;
    } else {
      console.log('⏳ VoIP token will be delivered via event');
      return null;
    }
  } catch (error) {
    console.error('❌ Failed to register for VoIP push:', error);
    return null;
  }
};

/**
 * Get current VoIP token (iOS)
 * @returns {Promise<string|null>}
 */
export const getVoIPToken = async () => {
  if (getPlatform() !== 'ios') return null;

  try {
    const result = await VoIPPushPlugin.getToken();
    return result.token || null;
  } catch (error) {
    console.error('❌ Failed to get VoIP token:', error);
    return null;
  }
};

/**
 * Add listener for VoIP token updates (iOS)
 * @param {Function} callback - Called with { token: string }
 * @returns {Function} Cleanup function
 */
export const addVoIPTokenListener = (callback) => {
  if (getPlatform() !== 'ios') {
    return () => {};
  }

  try {
    const handle = VoIPPushPlugin.addListener('tokenUpdated', callback);
    console.log('👂 VoIP token listener added');
    return () => {
      handle.remove();
      console.log('👋 VoIP token listener removed');
    };
  } catch (error) {
    console.error('❌ Failed to add VoIP token listener:', error);
    return () => {};
  }
};

/**
 * Add listener for incoming VoIP calls (iOS)
 * These arrive AFTER CallKit is already shown
 * @param {Function} callback - Called with { callId, callerId, callerName, callerAgency }
 * @returns {Function} Cleanup function
 */
export const addVoIPCallListener = (callback) => {
  if (getPlatform() !== 'ios') {
    return () => {};
  }

  try {
    const handle = CallKitPlugin.addListener('voipCallReceived', callback);
    console.log('👂 VoIP call listener added');
    return () => {
      handle.remove();
      console.log('👋 VoIP call listener removed');
    };
  } catch (error) {
    console.error('❌ Failed to add VoIP call listener:', error);
    return () => {};
  }
};

// ========================================
// Android Permission Functions
// ========================================

/**
 * Check if all required Android permissions are granted
 * @returns {Promise<{fullScreenIntent: boolean, notifications: boolean, allGranted: boolean}>}
 */
export const checkAndroidPermissions = async () => {
  if (getPlatform() !== 'android') {
    return { fullScreenIntent: true, notifications: true, allGranted: true };
  }

  try {
    const result = await FullScreenIntentPermission.checkAllPermissions();
    console.log('📋 Android permissions:', result);
    return result;
  } catch (error) {
    console.error('❌ Failed to check Android permissions:', error);
    return { fullScreenIntent: false, notifications: false, allGranted: false };
  }
};

/**
 * Request POST_NOTIFICATIONS permission (Android 13+)
 * @returns {Promise<boolean>} true if granted
 */
export const requestNotificationsPermission = async () => {
  if (getPlatform() !== 'android') return true;

  try {
    console.log('📲 Requesting POST_NOTIFICATIONS permission...');
    const result = await FullScreenIntentPermission.requestNotificationsPermission();
    console.log(result.granted ? '✅ Notifications permission granted' : '❌ Notifications permission denied');
    return result.granted === true;
  } catch (error) {
    console.error('❌ Failed to request notifications permission:', error);
    return false;
  }
};

/**
 * Request USE_FULL_SCREEN_INTENT permission (Android 12+)
 * Opens system settings - user must grant manually
 * @returns {Promise<boolean>} true if settings opened successfully
 */
export const requestFullScreenIntentPermission = async () => {
  if (getPlatform() !== 'android') return true;

  try {
    console.log('📲 Requesting USE_FULL_SCREEN_INTENT permission...');
    const result = await FullScreenIntentPermission.requestPermission();
    
    if (result.opened) {
      console.log('✅ Settings opened - user must grant permission manually');
      return true;
    }
    return false;
  } catch (error) {
    console.error('❌ Failed to request full-screen intent permission:', error);
    return false;
  }
};

/**
 * Request all required Android permissions
 * Shows appropriate prompts based on Android version
 * @returns {Promise<boolean>} true if all permissions granted
 */
export const requestAllAndroidPermissions = async () => {
  if (getPlatform() !== 'android') return true;

  try {
    // Step 1: Request POST_NOTIFICATIONS (Android 13+)
    const notificationsGranted = await requestNotificationsPermission();
    
    if (!notificationsGranted) {
      console.warn('⚠️ Notifications permission denied - calls will not work');
      return false;
    }

    // Step 2: Check full-screen intent permission
    const permissions = await checkAndroidPermissions();
    
    if (!permissions.fullScreenIntent) {
      console.log('⚠️ Full-screen intent permission needed - opening settings');
      await requestFullScreenIntentPermission();
      
      // Re-check after user returns from settings
      // (Note: We can't detect when they return, so this is best-effort)
      const updatedPermissions = await checkAndroidPermissions();
      return updatedPermissions.allGranted;
    }

    return true;
  } catch (error) {
    console.error('❌ Failed to request Android permissions:', error);
    return false;
  }
};

// ========================================
// Platform-Agnostic Helper Functions
// ========================================

/**
 * Initialize call notifications for the current platform
 * Call this once on app startup
 * @returns {Promise<{platform: string, ready: boolean, token?: string}>}
 */
export const initializeCallNotifications = async () => {
  const platform = getPlatform();
  console.log(`🔧 Initializing call notifications for ${platform}...`);

  if (platform === 'ios') {
    // Register for VoIP push and get token
    const token = await registerForVoIPPush();
    return {
      platform: 'ios',
      ready: true,
      token: token || undefined
    };
  } else if (platform === 'android') {
    // Check/request permissions
    const allGranted = await requestAllAndroidPermissions();
    return {
      platform: 'android',
      ready: allGranted
    };
  } else {
    // Web or unknown platform
    return {
      platform,
      ready: false
    };
  }
};

/**
 * Format caller name for display
 * @param {string} name - Caller's name
 * @param {string} agency - Caller's agency
 * @returns {string} Formatted display name
 */
export const formatCallerName = (name, agency) => {
  if (!name) return 'Unknown Caller';
  if (!agency || agency === 'Unknown Agency') return name;
  return `${name} — ${agency}`;
};

export default {
  // Platform detection
  getPlatform,
  isNative,
  isCallKitAvailable,
  
  // iOS CallKit
  reportIncomingCall,
  reportOutgoingCall,
  reportOutgoingCallConnected,
  endCall,
  reportCallEnded,
  setCallKitSpeakerRoute,
  addCallKitListener,
  
  // iOS VoIP Push
  registerForVoIPPush,
  getVoIPToken,
  addVoIPTokenListener,
  addVoIPCallListener,
  
  // Android Permissions
  checkAndroidPermissions,
  requestNotificationsPermission,
  requestFullScreenIntentPermission,
  requestAllAndroidPermissions,
  
  // Helpers
  initializeCallNotifications,
  formatCallerName
};
